import type { FC, PropsWithChildren } from 'react';
import { Suspense, useEffect } from 'react';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { Redirect } from '@xing-com/crate-core-router';
import {
  EditContextProvider,
  ErrorContextProvider,
  PageContextProvider,
  PageError,
  PageLayout,
  useTrackingVisitor,
} from '@xing-com/crate-entity-pages-common';
import { Appcues } from '@xing-com/crate-entity-pages-entity-pages-shared-appcues';
import { Header } from '@xing-com/crate-entity-pages-header';

import { MainPageLayout } from './app-layout';
import { MetaDataContainer } from './components/main-page-metadata/main-page-metadata';
import { useEntityPage } from './hooks/use-entity-page/use-entity-page';

const AppContexts: FC<
  PropsWithChildren<{
    pageSlug: string;
  }>
> = ({ children, pageSlug }) => {
  const { trackVisitor } = useTrackingVisitor({ pageSlug });

  useEffect(() => {
    trackVisitor();
  }, []);

  const { data, redirectTo, errorCode } = useEntityPage({ pageSlug });

  if (redirectTo) return <Redirect to={redirectTo} />;
  if (errorCode || !data) return <PageError type={errorCode} />;

  return (
    <ErrorContextProvider>
      <PageContextProvider initialValue={data}>
        <EditContextProvider>
          {/* TODO: Appcues should be a hook as well */}
          <Appcues>{children}</Appcues>
        </EditContextProvider>
      </PageContextProvider>
    </ErrorContextProvider>
  );
};

const AppFallback: FC = () => {
  return (
    <>
      <MetaDataContainer setDefault={true} />
      <Header.Skeleton />
    </>
  );
};

export type AppProps = {
  pageSlug: string;
};
export const App: FC<AppProps> = ({ pageSlug }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageLayout>
      <ErrorContextProvider>
        <ErrorBoundary onError={() => <PageError />}>
          <Suspense fallback={<AppFallback />}>
            <AppContexts pageSlug={pageSlug}>
              <MainPageLayout />
            </AppContexts>
          </Suspense>
        </ErrorBoundary>
      </ErrorContextProvider>
    </PageLayout>
  );
};
